import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomerInfo } from 'redux/actions'
import SubHeader from 'components/SubHeader';
import ModalResetPassword from 'screens/ModalResetPassword';
import ModalRemoveTenant from 'screens/ModalRemoveTenant';
import FooterBar from 'components/FooterBar';
import Spinner from 'components/Spinner';
import Button from 'components/Button';
import GuestInformation from 'components/BookingMain/GuestInformation';
import GuestInformationImages from 'components/BookingMain/GuestInformationImages';
import SendCustomerMessage from './components/SendCustomerMessage';
import CommunicationHistory from './components/CommunicationHistory';
import MetaInformation from './components/MetaInformation';
import BookingHistory from './components/BookingHistory';
import GeneralTenantNotes from './components/GeneralTenantNotes';
import { useQuery } from '@apollo/client';
import { GET_TENANT_INFO_BY_TENANT_ID } from 'graphql/service';
import { useCustomerForm } from 'customHook/useCustomerForm';
import { scrollDown } from 'utils/scrollDown';
import {
  Content,
  ContentColumn,
  Container,
  DesktopCommunicationContainer,
  MobileCommunicationContainer
} from 'components/BookingMain/styles';
import ChatIcon from 'assets/icons/chat.svg'
import { debouncedErrorMessage } from 'utils/messageUtil';
import { te } from 'intl-tel-input/i18n';

const TenantDetails = () => {

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const customerInfo = useSelector(state => state.customerInfoReducer.customerInfo)
  const tenantId = useParams().tenantId
  const screenBottomRef = useRef()

  //const scrollToBottom = () => screenBottomRef.current.scrollIntoView({ behavior: 'smooth' });
  const scrollToBottom = () => scrollDown(screenBottomRef.current?.offsetTop, 1500)  

  //GeneralTenantNotes
  const [tenantNotes, setTenantNotes] = useState([])

  const { data: tenantInfoData, loading, error } = useQuery(GET_TENANT_INFO_BY_TENANT_ID, {
    variables: { tenantId: tenantId }
  })

  const tenant = tenantInfoData?.tenantByID
  const groups = tenant?.groups || []

  useEffect(() => {

    if (!tenant) return;
    setTenantNotes(tenant.tenantNotes ?? []);

    const newCustomer = {
      ...customerInfo,
      userId: tenant.user?.userId ?? '',
      tenantId,
      fullName: tenant.user?.fullName ?? '',
      email: tenant.user?.email ?? '',
      phoneNumber: tenant.user?.phoneNumber ?? '',
      streetAddress: tenant.user?.address ?? '',
      streetAddressLine2: tenant.user?.addressLine2 ?? '',
      city: tenant.user?.city ?? '',
      province: tenant.user?.province ?? '',
      postalCode: tenant.user?.postalCode ?? '',
      country: tenant.user?.country ?? '',
      dateOfBirth: tenant.user?.dateOfBirth ?? '',
      travelDocumentNumber: '', //FIXME:
      travelDocumentType: '',   //FIXME:
    };

    dispatch(setCustomerInfo(newCustomer));
  }, [tenant, customerInfo, tenantId, dispatch]);

  useEffect(() => {
    if (!error) return;

    if (!error.networkError) {
      let content = error.message;

      if (error.message.includes('Validation')) {
        content = JSON.stringify(
          error.graphQLErrors[0]?.extensions?.exception
        );
      }

      debouncedErrorMessage(content);
    }
  }, [error]);

  useEffect(() => {
    if(location.state?.communication) {
      scrollToBottom()
    }
  },[location.state?.communication])

  //Customer Information
  const [customerInfoError, setCustomerInfoError] = useState({})
  const { updateCustomerInfo, updateUserInfoLoading } = useCustomerForm(setCustomerInfoError)

  if (loading) return <Spinner />

  const handleSaveChanges = () => {
    if (updateUserInfoLoading) return
    updateCustomerInfo()
  }

  return (
    <>
      <Helmet>
        <title>Tenant Details - Rook Hotel PMS</title>
        <meta name="description" content="Tenant Details - Rook Hotel PMS" />
      </Helmet>
      <SubHeader>
        <div className='title'>
          Guest Profile
        </div>
        <div className='buttonContainer'>
          <div>
            <ModalResetPassword
              buttonLabel="Reset Password"
              buttonStyle={`
                width: 122px;
                margin-left: 14px;
              `}
              email={customerInfo.email}
              phoneNumber={customerInfo.phoneNumber}
            />
            <ModalRemoveTenant
              buttonLabel="Remove"
              buttonStyle={`
                width: 115px;
                margin-left: 14px;
              `}
              name={customerInfo.fullName}
              tenantId={customerInfo.tenantId}
            />
          </div>
        </div>
      </SubHeader>
      <Content
        additionalstyle={`
          margin-top: 100px;
        `}
      >
        <MobileCommunicationContainer>
          <Button
            mainPage
            onClick={(event)=>{
              if (event.metaKey || event.ctrlKey) {
                // Open new tab with absolute URL
                window.open(`https://pms.rookhotel.com/tenant-communications/${tenantId}`, '_blank', 'noopener,noreferrer')
              } else {
                navigate(`/tenant-communications/${tenantId}`)
              }
            }}
            additionalstyle={`

              @media only screen and (min-width: 767px) and (max-width: 1200px) {
                width: 308px;
                height: 55px;
                font-size: 15px;
              }

              @media only screen and (max-width: 767px) {
                width: 100%;
                height: 55px;
                font-size: 15px;
              }
            `}
          >
            <img src={ChatIcon} alt={'chat'} width={29} height={29}/>
            {`Communications with ${customerInfo.fullName}`}
          </Button>
        </MobileCommunicationContainer>
        <ContentColumn>
          <Container>
            <GuestInformation
              customerInfoError={customerInfoError}
              setCustomerInfoError={setCustomerInfoError}
              btnInfo={
                <Button
                  mainPage
                  success
                  onClick={handleSaveChanges}
                  data-loading={updateUserInfoLoading}
                  additionalstyle={`
                    margin-left: 14px;
                    width: 113px;
                  `}
                >
                  {updateUserInfoLoading ? null: 'Save Changes'}
                </Button>
              }
            />
          </Container>
          <Container>
            <GuestInformationImages />
          </Container>
        </ContentColumn>
        <ContentColumn>
          <Container>
            <BookingHistory groups={groups} />
          </Container>
          <Container>
            <GeneralTenantNotes
              notes={tenantNotes}
              tenantId={tenantId}
              setNotes={setTenantNotes}
            />
          </Container>
          <Container>
            <MetaInformation
              created={Date.now()}
              origin={"Booking.com"}
            />
          </Container>
        </ContentColumn>
        <DesktopCommunicationContainer ref={screenBottomRef}>
          <SendCustomerMessage />
          <CommunicationHistory tenantId={tenantId}/>
        </DesktopCommunicationContainer>
      </Content>
      <FooterBar />
    </>
  )
}

export default TenantDetails;
